/**
 * Image Helpers
 *
 * Pure stateless image utilities. No store access.
 *
 * Migrated from: src/mixins/global.js
 */

import DEFAULT_CUSTOMER_IMAGE from '@/resources/images/default-customer.svg'
import PRODUCT_PLACEHOLDER_IMAGE from '@/resources/images/product_placeholder.svg'

/**
 * Matches only safe image URL schemes:
 *  - http:// and https://  (absolute remote URLs)
 *  - data:image/(png|jpeg|gif|webp);base64,  (raster base64 data URIs only —
 *    excludes svg+xml and non-base64 encodings to prevent active-content injection)
 * Relative paths (no scheme character) are also allowed as internal assets.
 */
const SAFE_IMAGE_SCHEME_RE = /^(https?:\/\/|data:image\/(png|jpe?g|gif|webp);base64,)/i

function isSafeImageUrl(url: string): boolean {
  // If the string contains a colon it carries a scheme — only permit safe ones.
  if (url.indexOf(':') !== -1) return SAFE_IMAGE_SCHEME_RE.test(url)
  // Relative URLs have no scheme; require at least one dot as a basic sanity check.
  return url.indexOf('.') !== -1
}

/**
 * Returns the input URL if it is a safe image path (http/https, data:image, or
 * a relative path containing a dot), otherwise returns the default customer avatar.
 */
export function checkIfThereIsNoLogoThenShowTransparentImage(input: string | string[] | undefined | null): string {
  if (input?.length && Array.isArray(input)) input = input[0]
  if (isSafeImageUrl((input as string) ?? '')) return input as string
  return DEFAULT_CUSTOMER_IMAGE
}

/**
 * Parses an image field that may be a raw Python list string or a plain URL,
 * and returns a usable image URL.
 */
export function renderImage(input: string | string[] | undefined | null): string | undefined {
  if (!input) return undefined

  if (typeof input === 'object' && input.length > 0) {
    const first = input[0]
    if (first.length >= 3) {
      const cleaned = first.split(', ')[0].replace('[', '').replace(']', '').replace(/'/g, '')
      return checkIfThereIsNoLogoThenShowTransparentImage(cleaned)
    }
  } else if (typeof input === 'string') {
    const cleaned = input.split(', ')[0].replace('[', '').replace(']', '').replace(/'/g, '')
    return checkIfThereIsNoLogoThenShowTransparentImage(cleaned)
  }

  return undefined
}

/**
 * Returns an inline CSS background style string for a customer image.
 */
export function renderCustomerImage_style_background(input: string | undefined | null): string | undefined {
  if (!input) return undefined
  const url = checkIfThereIsNoLogoThenShowTransparentImage(input)
  return `background: url(${url}) no-repeat;`
}

/**
 * Replaces a broken <img> src with a placeholder to avoid broken image icons.
 * Attach to `@error` on <img> elements.
 */
export function handleImageError(event: Event): void {
  const target = event?.target as HTMLImageElement | null
  if (target) {
    target.src = PRODUCT_PLACEHOLDER_IMAGE
    target.onerror = null
  }
}
